import { trackRedditLead } from "~/web/site/reddit-pixel";
import { formParts, turnstileSubmit } from "~/web/site/turnstile";
import { track } from "~/web/src/analytics";
import { keepFirstTouch } from "~/web/src/first-touch";
import { trackLead } from "~/web/src/google-analytics";
import { SITE_URL_HINT, siteUrl } from "~/web/src/site-url";

// The landing form on the home page (docs/project/own-auth-spec.md, decision 5). The
// browser validates the fields (required, email; the URL by siteUrl, scheme optional, through
// the same validity bubble); on submit the Turnstile widget (web/site/turnstile.ts) takes the
// fields' place and its token sends the request: POST /api/start {url, preset, email,
// turnstile} (the preset dropdown's key; the server resolves it to the mission, auth/routes.ts)
// — or {url, prompt, email, turnstile} when the dropdown is on "Something else…" (value ""),
// which reveals the free-text brief under it; left empty, the run is the product-demo preset.
// → the account is made and the browser follows the answer to /projects/<id>, where the run
// is already going. A known address is mailed a sign-in link instead (409, a note, not an
// error); a refusal (422/429, the sentence to show) or a failed widget brings the fields back
// with the message under them.

export function guestForm(form: HTMLFormElement): void {
  const parts = formParts(form);
  const urlInput = form.querySelector<HTMLInputElement>("input[name=url]");
  const select = form.querySelector<HTMLSelectElement>("select[name=preset]");
  const brief = form.querySelector<HTMLElement>(".maker-brief");
  if (!parts || !urlInput || !select || !brief) return;
  urlInput.addEventListener("input", () => urlInput.setCustomValidity(""));
  const syncBrief = () => {
    brief.hidden = select.value !== "";
  };
  select.addEventListener("change", () => {
    syncBrief();
    if (!brief.hidden) brief.querySelector("input")?.focus();
  });
  syncBrief(); // the browser may restore a previous pick on back/reload
  const field = (name: string) => String(new FormData(form).get(name) ?? "").trim();

  const { fail } = turnstileSubmit(parts, {
    validate: () => {
      if (siteUrl(field("url")) === null) {
        urlInput.setCustomValidity(SITE_URL_HINT);
        urlInput.reportValidity();
        return false;
      }
      return true;
    },
    send: async (turnstile) => {
      // Another tab may have written the cookie since this page loaded; the row records this tab's.
      await keepFirstTouch();
      let res: Response;
      let json: { projectId?: string; error?: string };
      try {
        res = await fetch("/api/start", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            url: siteUrl(field("url")) ?? field("url"),
            ...(field("preset") === "" ? { prompt: field("prompt") } : { preset: field("preset") }),
            email: field("email"),
            turnstile,
          }),
        });
        json = (await res.json().catch(() => ({}))) as typeof json;
      } catch {
        fail("Couldn't reach Featurette — check your connection and try again.");
        return;
      }
      if (!res.ok || typeof json.projectId !== "string") {
        track("start.refused", { status: res.status, reason: json.error ?? "" }, { instant: true });
        parts.error.classList.toggle("note", res.status === 409);
        fail(json.error ?? `Something went wrong (${res.status}). Try again in a moment.`);
        return;
      }
      track("start.requested", {}, { instant: true });
      trackRedditLead();
      trackLead();
      location.assign(`/projects/${encodeURIComponent(json.projectId)}`);
    },
  });
}
